import { useCallback, useEffect, useState } from 'react';

/**
 * Tres opciones y no dos.
 *
 * «Automático» no es un relleno entre claro y oscuro: es lo que hace la mayoría de los teléfonos
 * —cambian solos al atardecer— y sacarlo obligaría a elegir un tema fijo para las dos mitades del
 * día. Además es el valor por omisión, así que quien nunca abra esta pantalla obtiene lo que su
 * sistema ya decidió.
 */
export type Tema = 'auto' | 'light' | 'dark';

const CLAVE = 'fitlog:tema';

/** Lo que el sistema dice en este momento. */
function delSistema(): 'light' | 'dark' {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Lo elegido, o claro.
 *
 * El valor por omisión es CLARO y no automático, aunque automático exista como opción: es una
 * decisión tomada antes en este producto —la aplicación arranca en claro—, y cambiarla de
 * costado al agregar el selector habría alterado lo que ve todo el que nunca lo toque.
 */
function guardado(): Tema {
  const valor = window.localStorage.getItem(CLAVE);
  return valor === 'light' || valor === 'dark' || valor === 'auto' ? valor : 'light';
}

/**
 * Escribe el tema efectivo en el elemento raíz y en la barra del navegador.
 *
 * Vive fuera del hook para que `index.html` pueda llamarla antes de que React monte: aplicar el
 * tema recién en un efecto produce un destello blanco en cada arranque, que en oscuro es el
 * equivalente a que te prendan la luz en la cara.
 */
export function aplicarTema(tema: Tema): 'light' | 'dark' {
  const efectivo = tema === 'auto' ? delSistema() : tema;
  document.documentElement.setAttribute('data-theme', efectivo);

  // La barra del navegador acompaña. En claro es el naranja de marca; en oscuro, el fondo de la
  // aplicación, porque un naranja saturado arriba de una pantalla oscura es una franja encendida.
  const meta = document.querySelector('meta[name="theme-color"]');
  meta?.setAttribute('content', efectivo === 'dark' ? '#15140f' : '#dc6414');

  return efectivo;
}

export interface EstadoDelTema {
  tema: Tema;
  efectivo: 'light' | 'dark';
  elegir: (siguiente: Tema) => void;
}

export function useTheme(): EstadoDelTema {
  const [tema, setTema] = useState<Tema>(() => guardado());
  const [efectivo, setEfectivo] = useState<'light' | 'dark'>(() => aplicarTema(guardado()));

  const elegir = useCallback((siguiente: Tema) => {
    window.localStorage.setItem(CLAVE, siguiente);
    setTema(siguiente);
    setEfectivo(aplicarTema(siguiente));
  }, []);

  /*
   * En automático hay que seguir escuchando: el teléfono cambia solo al atardecer, y una pantalla
   * abierta en ese momento se quedaría en el tema de hace una hora.
   */
  useEffect(() => {
    if (tema !== 'auto') {
      return;
    }

    const consulta = window.matchMedia('(prefers-color-scheme: dark)');
    const alCambiar = () => setEfectivo(aplicarTema('auto'));

    consulta.addEventListener('change', alCambiar);
    return () => consulta.removeEventListener('change', alCambiar);
  }, [tema]);

  return { tema, efectivo, elegir };
}
